import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    getAllOrders,              // Fetches all orders from the API
    updateOrderStatus,       // Uses the API to update the order status
    updateOrderPayment,      // Uses the API to update total and advance payment
} from '../api/tailorApi';

// ===========================================
// --- ⚙️ HELPER MODAL COMPONENTS ---
// ===========================================

// Placeholder for OrderItemDetails component
const OrderItemDetails = ({ item }) => (
    <div className="border border-gray-200 bg-gray-50 p-3 rounded-md shadow-sm mb-2">
        <h5 className="font-semibold text-gray-800">{item.costume_name || 'Item Details'}</h5>
        <p className="text-xs text-gray-500">Quantity: **{item.quantity || 1}**, Price: ₹{item.price || 0}</p>
    </div>
);

// --- StatusUpdateModal Component (Unchanged) ---
const StatusUpdateModal = ({ isOpen, onClose, order, onUpdateSuccess }) => {
    const [newStatus, setNewStatus] = useState(order?.status || 'Pending');
    const [isUpdating, setIsUpdating] = useState(false);
    const statusOptions = ['Pending', 'Completed', 'Cancelled', 'Trial', 'On Order'];

    useEffect(() => {
        if (order) {
            setNewStatus(order.status);
        }
    }, [order]);

    if (!isOpen || !order) return null;

    const handleStatusChange = (e) => {
        setNewStatus(e.target.value);
    };

    const handleSubmit = async () => {
        if (newStatus === order.status) {
            alert("Status is already set to " + newStatus);
            return;
        }

        setIsUpdating(true);
        try {
            await updateOrderStatus(order.id, newStatus);
            onUpdateSuccess(order.id, { status: newStatus });
            onClose();
        } catch (error) {
            console.error("Failed to update order status:", error);
            alert(`Error updating status: ${error.message}`);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">✅ Update Status for Order #{order.id}</h2>
                <p className="text-sm text-gray-600 mb-4">Current Status: <span className="font-semibold">{order.status}</span></p>

                <div className="mb-6">
                    <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-1">Select New Status</label>
                    <select
                        id="status-select"
                        value={newStatus}
                        onChange={handleStatusChange}
                        disabled={isUpdating}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border"
                    >
                        {statusOptions.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>

                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isUpdating}
                        className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        Close
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isUpdating}
                        className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50"
                    >
                        {isUpdating ? 'Saving...' : 'Save New Status'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- 💰 PaymentEditModal Component (Unchanged) ---
const PaymentEditModal = ({ isOpen, onClose, order, onUpdateSuccess }) => {
    const [newTotal, setNewTotal] = useState(order?.total_amount || 0);
    const [newAdvance, setNewAdvance] = useState(order?.advance_payment || 0);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (order) {
            setNewTotal(order.total_amount);
            setNewAdvance(order.advance_payment);
        }
    }, [order]);

    if (!isOpen || !order) return null;

    const currentBalanceDue = (newTotal - newAdvance).toFixed(2);
    const isPaid = parseFloat(currentBalanceDue) <= 0;

    const parseNumericInput = (value) => {
        if (value === '' || value === null) {
            return 0;
        }
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue >= 0) {
            return numericValue;
        }
        return 0;
    };

    const handleTotalChange = (e) => {
        const value = parseNumericInput(e.target.value);
        setNewTotal(value);
    };

    const handleAdvanceChange = (e) => {
        const value = parseNumericInput(e.target.value);
        setNewAdvance(value);
    };

    const handleSubmit = async () => {
        if (newAdvance > newTotal) {
            alert("Error: Advance payment cannot exceed the total order amount.");
            return;
        }

        if (newTotal === order.total_amount && newAdvance === order.advance_payment) {
            alert("No changes detected.");
            onClose();
            return;
        }

        setIsUpdating(true);
        try {
            await updateOrderPayment(order.id, newTotal, newAdvance);

            onUpdateSuccess(order.id, {
                total_amount: newTotal,
                advance_payment: newAdvance
            });
            onClose();
        } catch (error) {
            console.error("Failed to update order payment:", error);
            alert(`Error recording payment: ${error.message}`);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">💰 Edit Payment for Order #{order.id}</h2>

                <div className="space-y-4 mb-6">
                    {/* Display Initial State */}
                    <div className="bg-blue-50 p-3 rounded">
                        <p className="text-sm font-semibold text-gray-700">Initial Total: ₹{order.total_amount.toLocaleString('en-IN')}</p>
                        <p className="text-sm font-semibold text-gray-700">Initial Advance: ₹{order.advance_payment.toLocaleString('en-IN')}</p>
                    </div>

                    {/* Total Amount Input */}
                    <div>
                        <label htmlFor="new-total" className="block text-sm font-medium text-gray-700 mb-1">Update Total Order Amount (₹)</label>
                        <input
                            id="new-total"
                            type="text"
                            value={newTotal === 0 ? '' : newTotal}
                            onChange={handleTotalChange}
                            inputMode="decimal"
                            disabled={isUpdating || order.status === 'Cancelled'}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md border"
                        />
                    </div>

                    {/* Advance/Paid Amount Input */}
                    <div>
                        <label htmlFor="new-advance" className="block text-sm font-medium text-gray-700 mb-1">Update Advance/Paid Amount (₹)</label>
                        <input
                            id="new-advance"
                            type="text"
                            value={newAdvance === 0 ? '' : newAdvance}
                            onChange={handleAdvanceChange}
                            inputMode="decimal"
                            disabled={isUpdating || order.status === 'Cancelled'}
                            placeholder={`Max: ${newTotal}`}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md border"
                        />
                    </div>

                    {/* Display Final Balance */}
                    <p className={`text-xl font-bold pt-2 border-t border-gray-200 ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                        Final Balance Due: {isPaid ? 'PAID' : `₹${parseFloat(currentBalanceDue).toLocaleString('en-IN')}`}
                    </p>
                </div>

                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isUpdating}
                        className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        Close
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isUpdating || order.status === 'Cancelled'}
                        className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${order.status === 'Cancelled' ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'} transition disabled:opacity-50`}
                    >
                        {isUpdating ? 'Saving...' : 'Save Payment Details'}
                    </button>
                </div>
            </div>
        </div>
    );
};


// --- PrintableEmployeeDetailView Component (Unchanged) ---
const PrintableEmployeeDetailView = ({ item, index }) => {
    const employeeName = item.employee_name || 'Unassigned';
    const measurementsArray = Array.isArray(item.measurements) ? item.measurements : [];
    const hasAssignment = !!item.assigned_employee_id;

    return (
        <div
            className="mb-4 pt-2 border-t border-black border-dashed print:mb-0 print:border-t-2 print:border-black print:border-dashed print:pt-2 print:text-xs"
            // *** FIX: Use CSS properties to prevent breaking inside this element ***
            style={{
                pageBreakInside: 'avoid !important',
                WebkitPrintColorAdjust: 'exact',
                breakInside: 'avoid',
            }}
        >

            <div className="flex justify-between text-sm font-bold mb-1 print:text-black">
                <span className="truncate w-1/2">Item **{index + 1}**: {item.costume_name || 'N/A'}</span>
                <span className="text-right">Qty: {item.quantity || 1}</span>
            </div>
            <p className="text-xs mb-2 print:text-black">Code: {item.costume_code || 'N/A'}</p>

            <div className="text-xs mb-2 p-1 border border-gray-300 print:border-black print:text-black">
                <p className="font-semibold truncate">Worker: {employeeName}</p>
                <p className="font-semibold">Status: {hasAssignment ? 'Assigned' : 'UNASSIGNED'}</p>
            </div>

            <div className="mt-2">
                <p className="text-xs font-bold underline mb-1 print:text-black">
                    Measurements:
                </p>
                {measurementsArray.length > 0 ? (
                    <div className="text-xs space-y-1">
                        {measurementsArray.map((m, i) => (
                            <div key={i} className="flex justify-between border-b border-dotted border-gray-400 print:border-black">
                                <span className="w-1/2">{m.measurement_name}:</span>
                                <span className="font-mono font-bold">{m.measurement_value} {m.unit || 'cm/in'}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs italic text-gray-500 print:text-black">
                        No specific measurements.
                    </p>
                )}
            </div>

            <div className="mt-3 pb-2 border-b-2 border-dashed border-gray-300 print:border-b-2 print:border-black print:border-dashed"></div>
        </div>
    );
};

// --- EmployeeDetailsModal Component (Unchanged) ---
const EmployeeDetailsModal = ({ isOpen, onClose, order }) => {
    const componentRef = useRef();

    if (!isOpen || !order) return null;

    const orderItems = Array.isArray(order.items) ? order.items : [];

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white print:overflow-auto">

            {/* SCREEN VIEW (HIDDEN ON PRINT) */}
            <div className="p-6 rounded-xl shadow-2xl w-full max-w-4xl border-t-4 border-blue-500 bg-white print:hidden">
                <h2 className="text-2xl font-extrabold mb-6 text-gray-900 font-sans border-b pb-3">
                    Production Statement Preview - Order {order.id}
                </h2>

                <div
                    ref={componentRef}
                    className="max-h-[70vh] overflow-y-auto p-4 border border-gray-200 mx-auto max-w-sm"
                >
                    <div className="p-4">
                        <p className="text-lg font-bold text-gray-800 mb-2">Customer: {order.customer_name || 'N/A'}</p>
                        <p className="text-sm text-gray-600 mb-1">Order Date: {order.order_date || 'N/A'}</p>
                        <p className="text-sm text-gray-600 mb-4">Delivery Date: {order.delivery_date || 'N/A'}</p>
                        <h3 className="text-xl font-semibold mb-3 border-b pb-1">Item Production Details:</h3>

                        <div className="grid grid-cols-1 gap-4">
                            {orderItems.map((item, index) => (
                                <PrintableEmployeeDetailView key={index} item={item} index={index} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 space-x-3">
                    <button
                        onClick={onClose}
                        className="py-2 px-6 rounded-lg shadow-md text-sm transition bg-gray-800 hover:bg-gray-700 text-white font-semibold"
                    >
                        Close
                    </button>
                    <button
                        onClick={handlePrint}
                        className="py-2 px-6 rounded-lg shadow-md text-sm transition bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m0 0v2a2 2 0 002 2h4a2 2 0 002-2v-2m-4-10H9M7 17h10"></path></svg>
                        Print Receipt
                    </button>
                </div>
            </div>

            {/* PRINT VIEW (RECEIPT FORMAT) */}
            <div
                className="hidden print:block font-mono text-black print:text-xs"
                style={{
                    width: '80mm',
                    margin: '0 auto',
                    padding: '0 5px',
                    minHeight: '100%',
                    pageBreakAfter: 'auto',
                }}
            >
                <h1 className="text-lg font-extrabold mb-1 text-black text-center uppercase border-b border-dashed border-black pt-2">
                    Production Statement
                </h1>
                <p className="text-center text-xs mb-2">Order No: #{order.id}</p>

                <div className="space-y-1 mb-2 border-b border-dashed border-black pb-2 text-xs">
                    <p className="flex justify-between">
                        <span className="font-semibold">Customer:</span> <span>{order.customer_name || 'N/A'}</span>
                    </p>
                    <p className="flex justify-between">
                        <span className="font-semibold">Order Date:</span> <span>{order.order_date || 'N/A'}</span>
                    </p>
                    <p className="flex justify-between">
                        <span className="font-semibold">Delivery Date:</span> <span className="font-bold">{order.delivery_date || 'N/A'}</span>
                    </p>
                    <p className="flex justify-between">
                        <span className="font-semibold">Status:</span> <span>{order.status || 'Pending'}</span>
                    </p>
                </div>

                <h3 className="text-sm font-bold mb-1 border-b border-dashed border-black pb-1 uppercase">Item Details</h3>

                {orderItems.map((item, index) => (
                    <PrintableEmployeeDetailView key={index} item={item} index={index} />
                ))}

                <div className="mt-4 text-xs text-center border-t-2 border-dashed border-black pt-2 pb-2">
                    <p>-- PRODUCTION DETAILS RECEIPT --</p>
                    <p>KEEP FOR WORK ASSIGNMENT</p>
                </div>
            </div>

            <style jsx global>{`
                @media print {
                    @page {
                        size: auto;
                        margin: 0;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        background: #fff;
                    }
                }
            `}</style>

        </div>
    );
};


// ===========================================
// --- 📦 MAIN OrderManager Component ---
// ===========================================

const OrderManager = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOrder, setModalOrder] = useState(null);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
    const [filterStatus, setFilterStatus] = useState('All');

    const cardRefs = useRef({});
    const orderIdToManage = location.state?.orderIdToManage;

    // --- Data Fetching ---
    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAllOrders();

            if (Array.isArray(data)) {
                const formattedOrders = data.map(order => ({
                    ...order,
                    id: String(order.id),
                    total_amount: parseFloat(order.total_amount) || 0,
                    advance_payment: parseFloat(order.advance_payment) || 0,
                    assigned_employee_id: order.assigned_employee_id || null,
                    total_messages_sent: parseInt(order.total_messages_sent) || 0, // Ensure numeric for new field
                }));
                setOrders(formattedOrders);
            } else {
                setError("Invalid data format received from API.");
                setOrders([]);
            }
        } catch (err) {
            console.error("Failed to fetch orders:", err);
            setError(err.message || "Failed to load orders.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    // --- Scrolling Logic (omitted for brevity) ---
    useEffect(() => {
        // Simple scroll to order logic
        if (orderIdToManage && !loading && orders.length > 0) {
            const element = cardRefs.current[orderIdToManage];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Optional: clear the state so it doesn't scroll again on re-render
                navigate(location.pathname, { replace: true, state: {} });
            }
        }
    }, [loading, orders, orderIdToManage, navigate, location.pathname]);


    // --- Handlers ---
    const handleUpdate = (orderId, updates) => {
        setOrders(prevOrders => prevOrders.map(order =>
            order.id === String(orderId) ? { ...order, ...updates } : order
        ));
        setIsStatusModalOpen(false);
        setIsPaymentModalOpen(false);
    };

    const handleStatusClick = (order) => {
        setModalOrder(order);
        setIsStatusModalOpen(true);
    };

    const handlePaymentClick = (order) => {
        setModalOrder(order);
        setIsPaymentModalOpen(true);
    };

    const handleEmployeeDetailsClick = (order) => {
        setModalOrder(order);
        setIsEmployeeModalOpen(true);
    };

    const handleInvoiceClick = (order) => {
        navigate(`/billing-screen`, {
            state: {
                orderData: order
            }
        });
    };

    // --- Filtering Logic ---
    const filteredOrders = orders.filter(order => {
        const balance = (order.total_amount - order.advance_payment).toFixed(2);
        const isPaid = parseFloat(balance) <= 0;

        if (filterStatus === 'Paid') {
            return isPaid;
        }
        if (filterStatus === 'Unpaid') {
            return !isPaid;
        }
        return true;
    });

    // --- Render Logic ---

    if (loading) return <div className="p-8 text-center text-xl text-gray-700">Loading All Orders...</div>
    if (error) return <div className="p-8 text-center text-xl text-red-600">🚨 Error: {error}</div>

    return (
        <div>
            <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">📋 Order Manager: All Orders ({filteredOrders.length} / {orders.length})</h1>
                <p className="text-sm text-gray-500 mb-6">Use the action buttons to update order status, payments, or generate an invoice.</p>

                {/* FILTER COMPONENT */}
                <div className="mb-6 flex flex-wrap items-center gap-3">
                    <label htmlFor="payment-filter" className="text-sm font-medium text-gray-700">Filter by Payment Status:</label>
                    <select
                        id="payment-filter"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="mt-1 block py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    >
                        <option value="All">All Orders</option>
                        <option value="Paid">Paid Orders</option>
                        <option value="Unpaid">Unpaid Orders</option>
                    </select>
                </div>

                {filteredOrders.length === 0 && (
                    <div className="p-8 text-center text-xl text-gray-700 border border-dashed rounded-lg">No orders match the current filter criteria.</div>
                )}

                <div className="space-y-6">
                    {filteredOrders.map((order) => {
                        const total = order.total_amount;
                        const advance = order.advance_payment;
                        const balance = (total - advance).toFixed(2);
                        const isCancelled = order.status === 'Cancelled';
                        const isPaid = parseFloat(balance) <= 0;

                        const statusColor = isCancelled ? 'bg-red-100 text-red-800' : (order.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800');

                        const cardBorderColor = isCancelled ? 'border-red-600' : (isPaid ? 'border-green-600' : 'border-gray-800');

                        const updateStatusClasses = "bg-blue-600 hover:bg-blue-700 text-white";
                        const paymentEditClasses = isCancelled ? "bg-gray-400 text-gray-600 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 text-white";
                        const invoiceClasses = isCancelled ? "bg-gray-400 text-gray-600 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700 text-white";
                        const employeeDetailsClasses = "bg-gray-800 hover:bg-gray-700 text-white font-semibold";
                        const disabledActionClasses = isCancelled ? "disabled:opacity-75 disabled:shadow-none" : "";

                        return (
                            <div
                                key={order.id}
                                ref={el => cardRefs.current[order.id] = el}
                                className={`bg-white p-4 sm:p-6 shadow-xl rounded-xl border-t-4 ${cardBorderColor} transition duration-300 hover:shadow-2xl`}
                            >

                                {/* 1. HEADER: Order & Customer Info (Mobile-Optimized Grid) */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pb-4 border-b border-gray-200 mb-4">
                                    <div className="col-span-1">
                                        <p className="text-xs font-medium text-gray-500">Order No</p>
                                        <p className="text-xl font-bold text-gray-900"> {order.id}</p>
                                    </div>
                                    <div className="col-span-1">
                                        <p className="text-xs font-medium text-gray-500">Customer</p>
                                        <p className="text-base font-semibold text-gray-800 truncate">{order.customer_name || 'N/A'}</p>
                                        <p className="text-xs text-gray-600">Ordered: {order.order_date || 'N/A'}</p>
                                    </div>
                                    <div className="col-span-1">
                                        <p className="text-xs font-medium text-gray-500">Delivery</p>
                                        <p className="text-base font-semibold text-red-600">**{order.delivery_date || 'N/A'}**</p>
                                    </div>
                                    <div className="col-span-1">
                                        <p className="text-xs font-medium text-gray-500">Trial</p>
                                        <p className="text-base font-semibold text-blue-600">**{order.trial_date || 'N/A'}**</p>
                                    </div>
                                    <div className="col-span-2 md:col-span-1 flex items-center justify-start md:justify-end">
                                        <span className={`inline-flex items-center px-4 py-1 rounded-full text-xs sm:text-sm font-medium ${statusColor}`}>
                                            Status: **{order.status || 'Pending'}**
                                        </span>
                                    </div>
                                </div>

                                {/* 2. FINANCIAL DETAILS (Stacked on Mobile) */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 bg-gray-50 p-4 rounded-lg">
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Total Amount</p>
                                        <p className="text-lg font-bold text-gray-800">₹{total.toLocaleString('en-IN')}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Advance/Paid</p>
                                        <p className="text-lg font-bold text-green-600">₹{advance.toLocaleString('en-IN')}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Balance Due</p>
                                        <p className={`text-xl font-bold ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                                            {isPaid ? 'PAID' : `₹${balance.toLocaleString('en-IN')}`}
                                        </p>
                                    </div>
                                </div>

                                {/* 2b. ADMINISTRATIVE & URGENCY DETAILS (Stacked on Mobile) */}
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Urgency</p>
                                        <p className={`text-lg font-bold ${order.urgency === 'High' ? 'text-red-700' : 'text-gray-700'}`}>
                                            {order.urgency || 'Normal'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Messages Sent</p>
                                        <p className="text-lg font-bold text-gray-700">{order.total_messages_sent || 0}</p>
                                    </div>
                                    <div className="col-span-full sm:col-span-2">
                                        <p className="text-xs font-medium text-gray-500">Last Status Message Sent</p>
                                        <p className="text-sm italic text-gray-600 truncate">{order.message_sent_for_current_status || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                                    <p className="text-xs font-medium text-gray-700 mb-1"> Notes</p>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{order.notes || 'No notes provided for this order.'}</p>
                                </div>

                                {/* 3. ITEMS & MEASUREMENTS */}
                                <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-4">Order Items ({order.items?.length || 0})</h3>
                                <div className="space-y-4">
                                    {(order.items || []).map((item, index) => (
                                        <OrderItemDetails key={index} item={item} />
                                    ))}
                                </div>

                                {/* Measurement Summary (Mobile Grid adjusted) */}
                                <div className="mt-6">
                                    <h4 className="text-lg font-bold text-gray-700 mb-3 border-b pb-1">📐 Measurement Summary</h4>
                                    {
                                        (order.items || []).map((item, index) => {
                                            const measurementsArray = Array.isArray(item.measurements) ? item.measurements : [];
                                            if (measurementsArray.length === 0) return null;

                                            return (
                                                <div key={`measurements-${index}`} className="mb-4 p-3 bg-indigo-50 border-l-4 border-indigo-300 rounded-lg">
                                                    <p className="font-semibold text-indigo-800 mb-2 text-sm">{item.costume_name || `Item ${index + 1}`} Measurements:</p>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                                        {measurementsArray.map((m, mIndex) => (
                                                            <div key={mIndex} className="bg-white p-2 rounded shadow-sm flex justify-between">
                                                                <span className="font-medium text-gray-600">{m.measurement_name}:</span>
                                                                <span className="font-bold text-indigo-600 ml-1">{m.measurement_value} cm</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                    {
                                        (order.items || []).every(item => !(Array.isArray(item.measurements) && item.measurements.length > 0)) &&
                                        (
                                            <p className="text-sm italic text-gray-500">No specific measurements recorded for this order.</p>
                                        )
                                    }
                                </div>

                                {/* 4. ACTION BUTTONS (Flex Wrap for Mobile) */}
                                <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap justify-end gap-3">

                                    <button
                                        onClick={() => handleEmployeeDetailsClick(order)}
                                        className={`py-2 px-4 rounded-lg shadow-md transition duration-150 text-sm ${employeeDetailsClasses}`}
                                    >
                                        View Employee Details
                                    </button>

                                    <button
                                        onClick={() => handleStatusClick(order)}
                                        className={`font-semibold py-2 px-4 rounded-md shadow-lg transition duration-150 text-sm ${updateStatusClasses}`}
                                    >
                                        Update Status
                                    </button>

                                    <button
                                        onClick={() => handlePaymentClick(order)}
                                        disabled={isCancelled}
                                        className={`font-semibold py-2 px-4 rounded-md shadow-lg transition duration-150 text-sm ${paymentEditClasses} ${disabledActionClasses}`}
                                    >
                                        Edit Payment
                                    </button>

                                    <button
                                        onClick={() => handleInvoiceClick(order)}
                                        disabled={isCancelled}
                                        className={`font-semibold py-2 px-4 rounded-md shadow-lg transition duration-150 text-sm ${invoiceClasses} ${disabledActionClasses}`}
                                    >
                                        Generate Invoice/Bill
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modals */}
            <StatusUpdateModal
                order={modalOrder}
                isOpen={isStatusModalOpen}
                onClose={() => setIsStatusModalOpen(false)}
                onUpdateSuccess={handleUpdate}
            />
            <PaymentEditModal
                order={modalOrder}
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                onUpdateSuccess={handleUpdate}
            />
            <EmployeeDetailsModal
                order={modalOrder}
                isOpen={isEmployeeModalOpen}
                onClose={() => setIsEmployeeModalOpen(false)}
            />
        </div>
    );
};

export default OrderManager;